import { computed, unref } from "vue";
import { useBadgeHoldersViewState } from "./useBadgeHoldersViewState";
import { resolveBadgeHolderStatus } from "./badgeHolderStatus";

function hasNoFiltersApplied(values) {
  return values.length === 0;
}

export function useTableConfig(
  filterLevels = [],
  onRemoveBadge = null,
  badgeLevelCounts = {},
  items = [],
  onRefreshBadges = null,
  hideActionButton = null,
  pageType = "responsibility",
  hideBadgeRemoveIcon = false,
  expirationIconReadOnly = false,
  onChatterClick = null
) {
  const viewState = useBadgeHoldersViewState();

  const resolvedHideActionButton = computed(() => {
    if (hideActionButton == null) return false;
    return !!unref(hideActionButton);
  });

  const hideSearchAndFilters = computed(() => {
    const itemsList = unref(items);
    const hasNoItems = itemsList.length === 0;
    const hasNoSearch = !viewState.state.filters.searchText;
    const hasNoFilters = hasNoFiltersApplied(viewState.state.filters.values);
    return hasNoItems && hasNoSearch && hasNoFilters;
  });

  const levelCounter = computed(() => {
    // Use unref to access the value whether it's a ref, reactive object, or plain object
    const counts = unref(badgeLevelCounts);
    const result = {
      apprentice: counts.apprentice ?? 0,
      professional: counts.professional ?? 0,
      coach: counts.coach ?? 0,
      master: counts.master ?? 0,
    };
    return result;
  });

  const tableParams = computed(() => {
    // Unwrap levelCounter to get the actual value and maintain reactivity
    const levelCounterValue = levelCounter.value;
    const pageTypeValue = unref(pageType) || "responsibility";
    const hideRemoveForReadOnly =
      pageTypeValue !== "responsibility" || !!unref(hideBadgeRemoveIcon);
    const expirationIconReadOnlyValue = !!unref(expirationIconReadOnly);

    return {
      headers: [
        {
          text: "Badge Holder",
          sortable: false,
          value: "name",
        },
        {
          text: "Badge Level",
          sortable: false,
          value: "level",
        },
        {
          text: "Issued By/On",
          sortable: false,
          value: "assignedBy",
          width: "150px",
        },
        {
          text: "Removed By/On",
          sortable: false,
          value: "removedBy",
          width: "150px",
        },
        {
          text: "Reason",
          sortable: false,
          value: "reason",
        },
      ],
      columns: [
        {
          field: "",
          component: "BadgeHolderCell",
          props: (item) => {
            return {
              class: "my-4",
              status: resolveBadgeHolderStatus(item),
              employee: item.employee,
              employeeStatus: item.employeeStatus,
              showStatusBadge: false,
              accountHover: item.accountHover || false,
              badgeStatus: item.badge?.lifecycleStatus || null,
              issuingMethod: item.badge?.issuingMethod || null,
            };
          },
          events: (item) => ({
            // Add events if needed
          }),
        },
        {
          field: "",
          component: "ReadinessBadge",
          props: (item) => {
            return {
              class: "my-4",
              badgeId: item.id ?? item.badge?.id ?? null,
              id: item.id ?? item.badge?.id ?? null,
              level: item.badge?.level || "Novice",
              dot: pageTypeValue === "responsibility" ? item.badge?.dot : "No Dot",
              view: pageTypeValue === "responsibility" ? item.badge?.view : "ReadOnly",
              expiration_date: item.badge?.expiration_date || null,
              expires_in_days_from_becoming_obsolete:
                item.badge?.expires_in_days_from_becoming_obsolete ?? null,
              has_expiration_if_obsolete: item.badge?.has_expiration_if_obsolete || false,
              plain: false,
              mini: false,
              upgraded: item.badge?.upgraded || false,
              upgradedVariantIcon: item.badge?.upgradedVariantIcon || null,
              unread: item.badge?.unread || 0,
              hideChatterIcon: pageTypeValue !== "responsibility",
              hideRemoveIcon: hideRemoveForReadOnly,
              hideIcons: pageTypeValue === "responsibility" ? false : true,
              expirationIconReadOnly: expirationIconReadOnlyValue,
              withoutFeatureFlag: pageTypeValue === "responsibility",
              badgeStatus: item.badge?.lifecycleStatus || null,
              issuingMethod: item.badge?.issuingMethod || null,
              endedOn: item.badge?.endedOn || null,
              endedBy: item.badge?.endedBy || null,
              endReason: item.badge?.endReason || "",
            };
          },
          events: (item) => ({
            removeBadge: () => {
              if (onRemoveBadge) {
                onRemoveBadge(item);
              }
            },
            expirationSynced: () => {
              const fn = unref(onRefreshBadges);
              if (typeof fn === "function") fn();
            },
            "chatter-click": (payload) => {
              const fn = unref(onChatterClick);
              if (typeof fn === "function") fn(item, payload);
            },
          }),
        },
        {
          field: "",
          component: "Stamp",
          props: (item) => {
            return {
              class: "my-4",
              name: item.assignedBy?.name || "",
              avatar: item.assignedBy?.picture || "",
              date: item.assignedBy?.displayDate || null,
              show_time: true,
            };
          },
          events: (item) => ({
            // Add events if needed
          }),
        },
        {
          field: "",
          component: "Stamp2",
          props: (item) => {
            return {
              class: "my-4",
              name: item.removedBy?.name || "",
              avatar: item.removedBy?.picture || "",
              date: item.removedBy?.displayDate || null,
              show_time: true,
            };
          },
          events: (item) => ({
            // Add events if needed
          }),
        },
        {
          field: "reason",
          component: null,
          props: (item) => {
            return {
              class: "my-4",
            };
          },
        },
      ],
      filters: {
        values: viewState.state.filters.values,
        searchText: viewState.state.filters.searchText,
        searchGhostText: viewState.state.filters.searchGhostText,
      },
      hideTopSection: true,
      hideSearchAndFilters: hideSearchAndFilters.value,
      hideActiveNo: true,
      showActionButton: resolvedHideActionButton.value,
      hideButtonInMessage: resolvedHideActionButton.value,
      activeRecords: {
        field: "status",
        value: "active",
      },
      defaultOrderingField: "issued_to__first_name",
      activeRecordsCount: 0,
      actionLabel: "Add Badge Holder",
      hideGridView: true,
      title: "",
      orderingStorageKey: "badgeHolders.ordering",
      showLevelCounter: true,
      levelCounter: levelCounterValue,
      filterLevels: filterLevels,
      customEmptyTitle: "No Badge Holders found",
      customEmptyDescription: "With the current search/filter, we could not find any Badge Holder",
      getRowStyle: (item) => ({
        backgroundColor: "#FFFFFF",
      }),
    };
  });

  return {
    tableParams,
  };
}
